import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  BOARD_REQUEST_JOINED,
  BOARD_REQUEST_NOT_JOINED,
  BOARD_REQUEST_PENDING,
  type BoardMembershipStatus,
} from '@/features/class/boardsApi';
/** Class IDs the current user has joined (approved membership). */
const SEED_JOINED_CLASS_IDS = ['class-sg-announcements'] as const;

export interface RequestJoinMeta {
  readonly classCode: string;
  readonly className: string;
  readonly ownerUsername: string;
}

interface ClassMembershipState {
  joinedClassIds: string[];
  requestedClassIds: string[];
  isJoined: (classId: string) => boolean;
  hasRequested: (classId: string) => boolean;
  isMember: (classId: string, options?: { isOwner?: boolean }) => boolean;
  joinOrRequest: (classId: string, isPrivate: boolean, meta: RequestJoinMeta) => void;
  setMembershipStatus: (classId: string, status: BoardMembershipStatus) => void;
  grantMembership: (classId: string) => void;
  cancelJoinRequest: (classId: string) => void;
  leaveClass: (classId: string) => void;
}

export const useClassMembershipStore = create<ClassMembershipState>()(
  persist(
    (set, get) => ({
      joinedClassIds: [...SEED_JOINED_CLASS_IDS],
      requestedClassIds: [],

      isJoined: (classId) => get().joinedClassIds.includes(classId),

      hasRequested: (classId) => get().requestedClassIds.includes(classId),

      isMember: (classId, options) => {
        if (options?.isOwner) {
          return true;
        }
        return get().joinedClassIds.includes(classId);
      },

      joinOrRequest: (classId, isPrivate, _meta) => {
        if (get().isJoined(classId) || get().hasRequested(classId)) {
          return;
        }

        if (isPrivate) {
          set((state) => ({
            requestedClassIds: [...state.requestedClassIds, classId],
          }));
          return;
        }

        set((state) => ({
          joinedClassIds: [...state.joinedClassIds, classId],
        }));
      },

      setMembershipStatus: (classId, status) => {
        if (status === BOARD_REQUEST_JOINED) {
          set((state) => ({
            joinedClassIds: state.joinedClassIds.includes(classId)
              ? state.joinedClassIds
              : [...state.joinedClassIds, classId],
            requestedClassIds: state.requestedClassIds.filter((id) => id !== classId),
          }));
          return;
        }

        if (status === BOARD_REQUEST_PENDING) {
          set((state) => ({
            joinedClassIds: state.joinedClassIds.filter((id) => id !== classId),
            requestedClassIds: state.requestedClassIds.includes(classId)
              ? state.requestedClassIds
              : [...state.requestedClassIds, classId],
          }));
          return;
        }

        if (status === BOARD_REQUEST_NOT_JOINED) {
          set((state) => ({
            joinedClassIds: state.joinedClassIds.filter((id) => id !== classId),
            requestedClassIds: state.requestedClassIds.filter((id) => id !== classId),
          }));
        }
      },

      grantMembership: (classId) => {
        set((state) => ({
          joinedClassIds: state.joinedClassIds.includes(classId)
            ? state.joinedClassIds
            : [...state.joinedClassIds, classId],
          requestedClassIds: state.requestedClassIds.filter((id) => id !== classId),
        }));
      },

      cancelJoinRequest: (classId) => {
        set((state) => ({
          requestedClassIds: state.requestedClassIds.filter((id) => id !== classId),
        }));
      },

      leaveClass: (classId) => {
        set((state) => ({
          joinedClassIds: state.joinedClassIds.filter((id) => id !== classId),
          requestedClassIds: state.requestedClassIds.filter((id) => id !== classId),
        }));
      },
    }),
    { name: 'myu-class-membership' },
  ),
);
